import type { SxProps, Theme } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";

import { onPrimarySurfaceColor } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";

import { PREVIOUS_CONSUMER_SESSION_SELECTOR } from "@/src/auth/previousSessionHint";

import { CONSUMER_HEADER_DESKTOP_QUERY } from "./consumerHeader.styles";

/**
 * Sizes of the real header items, measured from the rendered header, so each
 * placeholder holds the same slot as the item that replaces it.
 */
export const HEADER_SKELETON_SIZES = {
  /** Text links ("Usein kysyttyä", "Artikkelit", "Yhteystiedot") incl. 16px side padding. */
  navLinkWidths: ["180px", "135px", "165px"],
  navLinkPaddingX: "16px",
  navLinkHeight: "40px",
  navTextHeight: "14px",
  /** "Ostajille" and "Kirjaudu" pills. */
  actionPillWidth: "120px",
  /** "Kilpailutukset" pill with its count badge and dropdown arrow. */
  auctionsPillWidth: { desktop: "184px", mobile: "164px" },
  pillHeight: { desktop: "36px", mobile: "32px" },
  /**
   * Bell and account icon buttons: MUI's medium `IconButton` glyph (24px in
   * `pxToRem`, which the theme's rem base scales up) plus 8px padding a side.
   */
  iconGlyphPx: 24,
  iconSlotPaddingPx: 8,
  iconSize: "28px"
} as const;

/** Same radius as the theme's MuiButton root override. */
const PILL_RADIUS = "2rem";

const placeholderFill = (muiTheme: Theme) =>
  alpha(onPrimarySurfaceColor(muiTheme), 0.22);

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/**
 * Root of the header skeleton. It fades in after a short delay, so when auth
 * resolves quickly (no stored session) the placeholders never flash.
 */
export const headerSkeletonRootSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  opacity: 0,
  animation: `${fadeIn} 200ms ease-out 150ms forwards`,
  "@media (prefers-reduced-motion: reduce)": {
    opacity: 1,
    animation: "none"
  }
};

/** Shown only from the desktop breakpoint up, through CSS. */
export const headerSkeletonDesktopRowSx: SxProps<Theme> = {
  display: "none",
  alignItems: "center",
  gap: 1,
  [`@media ${CONSUMER_HEADER_DESKTOP_QUERY}`]: { display: "flex" }
};

/** Shown only below the desktop breakpoint, through CSS. */
export const headerSkeletonMobileRowSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  [`@media ${CONSUMER_HEADER_DESKTOP_QUERY}`]: { display: "none" }
};

/**
 * The guest and signed-in shapes are both rendered; the <html> attribute set
 * before the first paint picks one. `display: contents` keeps the items in the
 * row's flex layout and gap.
 */
export const headerSkeletonGuestShapeSx: SxProps<Theme> = {
  display: "contents",
  [`${PREVIOUS_CONSUMER_SESSION_SELECTOR} &`]: { display: "none" }
};

export const headerSkeletonUserShapeSx: SxProps<Theme> = {
  display: "none",
  [`${PREVIOUS_CONSUMER_SESSION_SELECTOR} &`]: { display: "contents" }
};

/** Slot of one text link; the bar inside stands in for its label. */
export const headerSkeletonNavLinkSx = (width: string): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  width,
  height: HEADER_SKELETON_SIZES.navLinkHeight,
  px: HEADER_SKELETON_SIZES.navLinkPaddingX,
  boxSizing: "border-box"
});

export const headerSkeletonTextSx: SxProps<Theme> = schemeSx((muiTheme) => ({
  width: "100%",
  height: HEADER_SKELETON_SIZES.navTextHeight,
  borderRadius: "4px",
  bgcolor: placeholderFill(muiTheme)
}));

export const headerSkeletonPillSx = (
  width: string,
  height: string
): SxProps<Theme> =>
  schemeSx((muiTheme) => ({
    width,
    height,
    borderRadius: PILL_RADIUS,
    bgcolor: placeholderFill(muiTheme)
  }));

/**
 * "Kilpailutukset" pill placeholder, sized per breakpoint through CSS so it
 * fits wherever it is rendered.
 */
export const headerSkeletonAuctionsPillSx: SxProps<Theme> = schemeSx(
  (muiTheme) => ({
    width: HEADER_SKELETON_SIZES.auctionsPillWidth.mobile,
    height: HEADER_SKELETON_SIZES.pillHeight.mobile,
    borderRadius: PILL_RADIUS,
    bgcolor: placeholderFill(muiTheme),
    [`@media ${CONSUMER_HEADER_DESKTOP_QUERY}`]: {
      width: HEADER_SKELETON_SIZES.auctionsPillWidth.desktop,
      height: HEADER_SKELETON_SIZES.pillHeight.desktop
    }
  })
);

export const headerSkeletonIconSlotSx: SxProps<Theme> = (muiTheme) => {
  const size = `calc(${muiTheme.typography.pxToRem(HEADER_SKELETON_SIZES.iconGlyphPx)} + ${2 * HEADER_SKELETON_SIZES.iconSlotPaddingPx}px)`;

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: size,
    height: size
  };
};

export const headerSkeletonIconSx: SxProps<Theme> = schemeSx((muiTheme) => ({
  width: HEADER_SKELETON_SIZES.iconSize,
  height: HEADER_SKELETON_SIZES.iconSize,
  bgcolor: placeholderFill(muiTheme)
}));
